<div class="flex flex-col" data-testid="default-style-settings">
  <h2 class="title-large text-primary mb-1" data-testid="style-settings-title">{{ 'Default style settings' | translate }}</h2>
  <p class="body-medium text-on-surface-variant mb-4 md:mb-8" data-testid="style-settings-description">
    {{ 'Customise the structure, tone, and language used in your transcripts' | translate }}
  </p>

  <form [formGroup]="styleForm" class="flex flex-col">
    <div class="flex flex-col lg:flex-row items-start">
      <div class="w-full lg:w-1/3 mb-4 md:mb-0">
        <div class="w-4/5">
          <div class="label-xlarge text-on-surface-variant">{{ 'Detail and style' | translate }}</div>
          <div class="flex flex-col gap-5">
            <div class="label-large text-outline">{{ 'Customise the phrasing and level of detail in your AI-generated content' | translate }}</div>
            <div class="label-large-prominent font-bold text-outline">{{ 'Note that any instructions in your blueprint will still take priority over these defaults' | translate }}</div>
          </div>
        </div>
      </div>
      <div class="w-full lg:w-2/3">
        <div class="w-full lg:w-4/5 flex flex-col">
          <div class="flex flex-col gap-8 pb-6 border-b border-surface-container">
            <app-option-slider
              variant="stops"
              [title]="'Detail level'"
              [options]="detailLevelOptions"
              [value]="styleForm.value.detailLevel"
              (valueChange)="onSliderChange('detailLevel', $event)">
            </app-option-slider>

            <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
              <mat-label>{{ 'Format mode' | translate }}</mat-label>
              <mat-select formControlName="formatMode" data-testid="format-mode-select">
                <mat-option *ngFor="let option of formatModeOptions" [value]="option.value">
                  {{ option.label | translate }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div class="py-6 border-b border-surface-container">
            <div class="bg-on-primary border border-surface-varient rounded-lg p-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:min-h-20" data-testid="abbreviation-policy">
              <div class="title-medium text-on-surface">{{ 'Abbreviations' | translate }}</div>
              <mat-radio-group formControlName="abbreviationPolicy" class="flex items-center gap-6" data-testid="abbreviation-policy-group">
                <mat-radio-button value="minimal">{{ 'Off' | translate }}</mat-radio-button>
                <mat-radio-button value="balanced">{{ 'On' | translate }}</mat-radio-button>
              </mat-radio-group>
            </div>
          </div>

          <div class="flex flex-col py-6 border-b border-surface-container">
            <app-option-slider
              variant="stops"
              [title]="'Quotations'"
              [options]="quotationOptions"
              [value]="styleForm.value.quotationPolicy"
              (valueChange)="onSliderChange('quotationPolicy', $event)">
            </app-option-slider>
          </div>

          <div class="bg-on-primary border border-surface-varient rounded-lg p-4 mt-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:min-h-20" data-testid="empty-section-policy">
            <div class="title-medium text-on-surface">{{ 'Empty sections' | translate }}</div>
            <mat-radio-group formControlName="emptySectionPolicy" class="flex items-center gap-6" data-testid="empty-section-policy-group">
              <mat-radio-button value="leave_blank">{{ 'Leave blank' | translate }}</mat-radio-button>
              <mat-radio-button value="omit">{{ 'Omit' | translate }}</mat-radio-button>
            </mat-radio-group>
          </div>
        </div>
      </div>
    </div>

    <div class="border-t border-surface-container my-6"></div>

    <div class="flex flex-col lg:flex-row items-start">
      <div class="w-full lg:w-1/3 mb-4 md:mb-0">
        <div class="w-4/5">
          <div class="label-xlarge text-on-surface-variant">{{ 'Preferred terms' | translate }}</div>
          <div class="label-large text-outline">{{ 'Preferred terms for clinicians and patients' | translate }}</div>
        </div>
      </div>
      <div class="w-full lg:w-2/3">
        <div class="w-full lg:w-4/5 flex flex-col gap-6">
          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>{{ 'Refer to clinician as' | translate }}</mat-label>
            <mat-select formControlName="referClinicianAs" data-testid="refer-clinician-select">
              <mat-option *ngFor="let option of clinicianOptions" [value]="option.value">
                {{ option.label | translate }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>{{ 'Refer to patient as' | translate }}</mat-label>
            <mat-select formControlName="referPatientAs" data-testid="refer-patient-select">
              <mat-option *ngFor="let option of patientOptions" [value]="option.value">
                {{ option.label | translate }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    </div>

    <div class="flex flex-col lg:flex-row items-start sticky bottom-0 z-10 pt-4 pb-6 bg-surface-container-lowest">
      <div class="hidden lg:block lg:w-1/3"></div>
      <div class="w-full lg:w-2/3 flex">
        <div class="w-full lg:w-4/5 flex items-end gap-4 justify-end">
          <button
            class="flex-1 [--mdc-outlined-button-container-height:36px] md:flex-none md:[--mdc-outlined-button-container-height:48px]"
            mat-stroked-button
            color="primary"
            type="button"
            (click)="cancel()"
            [disabled]="isLoading"
            data-testid="style-cancel-button">
            {{ 'Cancel' | translate }}
          </button>
          <button
            class="flex-1 [--mdc-filled-button-container-height:36px] md:flex-none md:[--mdc-filled-button-container-height:48px]"
            mat-flat-button
            color="primary"
            type="button"
            (click)="saveChanges()"
            [disabled]="!hasChanges || isLoading"
            data-testid="style-save-button">
            <app-loading-spinner [loading]="isLoading" [diameter]="20">
              {{ 'Save changes' | translate }}
            </app-loading-spinner>
          </button>
        </div>
      </div>
    </div>
  </form>
</div>
